import { Dialog, Transition } from '@headlessui/react'
import classNames from 'classnames'
import { isFunction } from 'lodash'
import { Fragment, useEffect, useRef } from 'react'
import useModalBackButton from '../../../../modules/hooks/common/useModalBackButton'
import usePopupStore from '../../../../store/usePopupStore'

export interface TranslationSidePopupPayload {
  id: string
  body: any
  title?: any
  onClose?: any
  overlayClose?: boolean
  noTopSide?: boolean //and X button disappear too.
  hFull?: boolean
  bgWhite?: boolean
  border?: boolean
  maxW?: number
  bgColor?: string
  noScrollbar?: boolean
  gapFromTop?: number
  className?: string
}

export const useTranslatorSidePopup = () => {
  const popupShow = usePopupStore((state) => state.show)
  const popupClose = usePopupStore((state) => state.close)

  const show = (props: TranslationSidePopupPayload) => {
    popupShow({ ...props, type: 'translatorSide' })
  }
  const close = (id) => {
    popupClose({ id })
  }
  return { show, close }
}

export const TranslatorSidePopup = ({ id }: { id: any }) => {
  const focusRef = useRef(null)
  const popupClose = usePopupStore((state) => state.close)
  const { data, isShow } = usePopupStore((state) => ({
    data: state.data[id],
    isShow: state.isPopupRendered,
  }))
  const open = !!data
  const {
    title = '',
    body = undefined,
    onClose = null,
    maxW = 448,
    overlayClose = true,
    noTopSide = false,
    hFull = false,
    bgWhite = false,
    border = false,
    bgColor = 'white',
    noScrollbar = true,
    gapFromTop = 70,
  } = data

  const closeHandler = () => {
    popupClose({ id })
    if (isFunction(onClose)) {
      onClose()
    }
  }

  useModalBackButton(id, isShow)

  useEffect(() => {
    return () => {
      popupClose({ id })
    }
  }, [])

  return (
    <div id={id}>
      <Transition appear show={open} as={Fragment}>
        <Dialog
          as="div"
          static
          className="z-2000 no-scrollbar fixed inset-0 flex items-end justify-end overflow-y-auto overflow-x-hidden"
          initialFocus={focusRef}
          open={open}
          onClose={() => {
            if (overlayClose) {
              closeHandler()
            }
          }}
        >
          <button ref={focusRef} className="hidden" />
          <div className="flex-1">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100">
              <Dialog.Overlay
                className={classNames('fixed inset-0 transition-opacity', {
                  'bg-white-overlay': bgWhite,
                })}
              />
            </Transition.Child>
            {/* This element is to trick the browser into centering the modal contents. */}
            <span aria-hidden="true" className="hidden sm:align-middle">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-x-20 sm:translate-y-0"
              enterTo="opacity-100 translate-x-0"
            >
              <div
                className="ml-auto transform overflow-hidden transition-all"
                style={{ maxWidth: maxW, boxShadow: '0px -2px 20px 0px rgba(0, 0, 0, 0.10)' }}
              >
                <div
                  style={{ height: hFull ? `calc(100vh - ${gapFromTop}px)` : '80vh' }}
                  className={classNames(
                    { 'border-1': border },
                    { 'no-scrollbar': noScrollbar },
                    'flex flex-col bg-white'
                  )}
                >
                  {noTopSide || (
                    <div className="flex items-start p-20">
                      <div>
                        <Dialog.Title
                          as="div"
                          className="whitespace-normal text-[20px] font-bold text-black"
                          style={{ wordBreak: 'keep-all' }}
                        >
                          {title}
                        </Dialog.Title>
                      </div>
                      <div className="ml-auto">
                        <button
                          type="button"
                          className="appearance-none border-none focus:outline-none"
                          style={{ width: 24, height: 24 }}
                          onClick={closeHandler}
                        >
                          <i className="fe fe-x"></i>
                        </button>
                      </div>
                    </div>
                  )}
                  {/* BODY */}
                  <div
                    className="p-20"
                    style={{ backgroundColor: bgColor || 'white', height: '100%', display: 'flex' }}
                  >
                    {body}
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}
